/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import { values } from 'mobx';
import { types } from 'mobx-state-tree';
import { BaseStore } from '@amzn/base-ui/dist/models/BaseStore';

import { getCatalogueProducts } from '../../helpers/api';
import { CatalogueProduct } from './CatalogueProduct';
import { CatalogueProductDetailStore } from './CatalogueProductDetailStore';

// ==================================================================
// CatalogueProductsStore
// ==================================================================
const CatalogueProductsStore = BaseStore.named('CatalogueProductsStore')
  .props({
    products: types.optional(types.map(CatalogueProduct), {}),
    detailStores: types.optional(types.map(CatalogueProductDetailStore), {}),
  })

  .actions(self => {
    // save the base implementation of cleanup
    const superCleanup = self.cleanup;

    return {
      async doLoad() {
        const products = await getCatalogueProducts();
        self.runInAction(() => {
          const productMap = {};
          products.forEach(product => {
            productMap[product.productId] = product;
          });
          self.products.replace(productMap);
        });
      },

      getDetailStore(productId) {
        let entry = self.detailStores.get(productId);
        if (!entry) {
          // Lazily create the store
          self.detailStores.set(productId, CatalogueProductDetailStore.create({ productId }));
          entry = self.detailStores.get(productId);
        }
        return entry;
      },

      cleanup: () => {
        self.products.clear();
        self.detailStores.clear();
        superCleanup();
      },
    };
  })

  .views(self => ({
    get list() {
      return values(self.products);
    },

    get total() {
      return self.products.size;
    },

    get empty() {
      return self.products.size === 0;
    },
  }));

function registerContextItems(appContext) {
  appContext.catalogueProductsStore = CatalogueProductsStore.create({}, appContext);
}

export { CatalogueProductsStore, registerContextItems };
